import {
  AddCircleOutlineRounded,
  DeleteOutlineRounded,
  RemoveCircleOutlineOutlined,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ReactComponent as EmptyCart } from "../../../../assets/svg/humcart_emptyCart.svg";
import * as CartActions from "../../../../redux/cart/cart.actions";
import * as CartReducer from "../../../../redux/cart/cart.reducers";
import {
  decrProductQtyAction,
  deleteCartAction,
  incrProductQtyAction,
} from "../../../../redux/cart/cart.reducers";
import { CartReduxService } from "../../../../redux/cart/CartReduxService";
import {
  AppDispatch,
  RootState,
  useAppDispatch,
} from "../../../../redux/store";
import * as UserReducer from "../../../../redux/users/users.reducers";
import { primary, secondary } from "../../../../themes/theme";
import LayoutHeading from "../../../layout/components/layout-heading/LayoutHeading";
import MainNavBar from "../../../layout/pages/navbar/MainNavBar";
import { IUserView } from "../../../users/models/IUserView";
import CheckoutButton from "../../components/checkout-button/CheckoutButton";
import { ICartRequestView } from "../../models/ICartRequestView";

function CartItemDetails(props: any) {
  return (
    <Grid container spacing={1} sx={{ mb: "5px" }}>
      <Grid item xs={5}>
        <Typography sx={{ fontFamily: "Ubuntu", fontWeight: "500" }}>
          {props.primary}
        </Typography>
      </Grid>
      <Grid item xs={7}>
        <Typography
          sx={{ fontFamily: "Ubuntu", fontWeight: "bold", textAlign: "right" }}
        >
          &#8377;
          {props.secondary}
        </Typography>
      </Grid>
    </Grid>
  );
}

const CartPage: React.FC = () => {
  const dispatch: AppDispatch = useAppDispatch();
  const navigate = useNavigate();

  //Get Cart Information from redux store
  const cartReduxState: CartReducer.InitialState = useSelector(
    (state: RootState) => {
      return state[CartReducer.cartFeatureKey];
    }
  );

  //Get Cart Information from redux store
  const userState: UserReducer.InitialState = useSelector(
    (state: RootState) => {
      return state[UserReducer.userFeatureKey];
    }
  );

  const { cartProduct } = cartReduxState;
  const { products } = cartProduct;

  const { user } = userState;

  const clickIncrementQty = (productId: string | undefined) => {
    if (productId) {
      dispatch({
        type: `${incrProductQtyAction}`,
        payload: { productId: productId },
      });
    }
  };

  const clickDecrementQty = (productId: string | undefined) => {
    if (productId) {
      dispatch({
        type: `${decrProductQtyAction}`,
        payload: { productId: productId },
      });
    }
  };

  const clickDeleteItem = (productId: string | undefined) => {
    if (productId) {
      dispatch({
        type: `${deleteCartAction}`,
        payload: { productId: productId },
      });
    }
  };

  const prepareCartRequestObject = (user: IUserView): ICartRequestView => {
    const cart: ICartRequestView = {
      products: products.map((product) => {
        if (product?._id) {
          return {
            product: product._id,
            count: product.count,
            price: product.price,
          };
        } else {
          throw new Error("Product ID is undefined");
        }
      }),
      tax: CartReduxService.calculateTaxUtil(products),
      total: CartReduxService.calculateTotalUtil(products),
      grandTotal: CartReduxService.calculateGrandTotal(products),
      userObj: user?._id,
    };

    return cart;
  };

  const clickCheckOut = () => {
    if (user && Object.keys(user).length > 0) {
      dispatch(
        CartActions.createCartAction({ cart: prepareCartRequestObject(user) })
      ).then((response: any) => {
        if (response && !response.error) {
          navigate("/cart/checkout");
        }
      });
    }
  };

  //Styles
  const cellStyle1 = {
    color: primary[800],
    fontWeight: "bold",
    fontFamily: "Ubuntu",
  };

  const cellStyle2 = {
    fontFamily: "Ubuntu",
    textTransform: "uppercase",
    Width: "100%",
  };

  return (
    <>
      <Box>
        <MainNavBar />
        <LayoutHeading
          heading="Your Cart"
          description="Seamlessly manage product additions, updates, and removals in a user-friendly cart interface."
        />
        {cartProduct.products.length > 0 ? (
          <Grid container sx={{ gap: "10px", px: { xs: 1, md: 9, lg: 10 } }}>
            <Grid
              component={Paper}
              elevation={0}
              item
              xs={12}
              md={7}
              sx={{ borderRadius: "10px" }}
            >
              <Card elevation={0}>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "Ubuntu",
                      pt: 1,
                      fontSize: "17px",
                      letterSpacing: "1px",
                      pl: 2,
                      fontWeight: "550",
                    }}
                  >
                    | Cart Items
                  </Typography>
                </Box>
                <Divider
                  variant="middle"
                  sx={{
                    mt: 1,
                    color: primary[900],
                    border: "1px dashed #f1f2f3",
                  }}
                />
                <CardContent sx={{ display: "flex", justifyContent: "center" }}>
                  <TableContainer
                    sx={{ borderRadius: "10px", background: secondary[100] }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow sx={{ background: secondary[300] }}>
                          <TableCell sx={{ ...cellStyle1 }}>S.NO</TableCell>
                          <TableCell sx={{ ...cellStyle1 }}>Image</TableCell>
                          <TableCell sx={{ ...cellStyle1 }}>Title</TableCell>
                          <TableCell sx={{ ...cellStyle1 }}>Price</TableCell>
                          <TableCell sx={{ ...cellStyle1 }}>Quantity</TableCell>
                          <TableCell sx={{ ...cellStyle1 }}>Total</TableCell>
                          <TableCell sx={{ ...cellStyle1 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cartProduct.products.map((product, index) => {
                          return (
                            <TableRow key={index}>
                              <TableCell sx={{ ...cellStyle2 }}>
                                {index + 1}
                              </TableCell>
                              <TableCell sx={{ ...cellStyle2 }}>
                                <img
                                  src={product?.imageUrl}
                                  alt="No Image"
                                  style={{
                                    width: "60px",
                                    height: "60px",
                                    borderRadius: "7px",
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ ...cellStyle2 }}>
                                <span
                                  style={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2, // Limit to 2 lines
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxHeight: "3em", // Adjust based on your line height
                                    lineHeight: "1.5em", // Adjust based on your preference
                                  }}
                                >
                                  {product?.title}
                                </span>
                              </TableCell>
                              <TableCell sx={{ ...cellStyle2 }}>
                                &#8377;{product?.price}
                              </TableCell>
                              <TableCell align="left" sx={{ ...cellStyle2 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    m: 0,
                                    p: 0,
                                    height: "100%",
                                    alignItems: "center",
                                  }}
                                >
                                  <IconButton
                                    sx={{
                                      background: "transparent",
                                      color: "yellowgreen",
                                      "&:hover": {
                                        background: "transparent",
                                      },
                                    }}
                                    onClick={() =>
                                      clickDecrementQty(product._id)
                                    }
                                  >
                                    <RemoveCircleOutlineOutlined />
                                  </IconButton>
                                  <span>{product.count}</span>
                                  <IconButton
                                    sx={{
                                      background: "transparent",
                                      color: "yellowgreen",
                                      "&:hover": {
                                        background: "transparent",
                                      },
                                    }}
                                    onClick={() =>
                                      clickIncrementQty(product._id)
                                    }
                                  >
                                    {" "}
                                    <AddCircleOutlineRounded />
                                  </IconButton>
                                </Box>
                              </TableCell>
                              <TableCell
                                sx={{ ...cellStyle2, fontWeight: "550" }}
                              >
                                &#8377;{product.count * product.price}
                              </TableCell>
                              <TableCell sx={{ ...cellStyle2 }}>
                                <IconButton
                                  sx={{
                                    background: "transparent",
                                    color: "orangered",
                                  }}
                                  onClick={() => clickDeleteItem(product._id)}
                                >
                                  <DeleteOutlineRounded />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid
              component={Paper}
              elevation={0}
              item
              xs={12}
              md={4}
              lg={4}
              xl={3}
              sx={{
                height: "100%",
                position: { xs: "none", md: "sticky" },
                top: 90,
                borderRadius: "10px",
              }}
            >
              <Card elevation={0}>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "Ubuntu",
                      pt: 1,
                      fontSize: "17px",
                      letterSpacing: "1px",
                      pl: 2,
                      fontWeight: "550",
                    }}
                  >
                    | Your Total
                  </Typography>
                </Box>
                <Divider
                  variant="middle"
                  sx={{
                    mt: 1,
                    color: primary[900],
                    border: "1px dashed #f1f2f3",
                  }}
                />
                <CardContent>
                  <CartItemDetails
                    primary={"Total"}
                    secondary={CartReduxService.calculateTotalUtil(
                      products
                    ).toFixed(2)}
                  />
                  <CartItemDetails
                    primary={"Tax"}
                    secondary={CartReduxService.calculateTaxUtil(
                      products
                    ).toFixed(2)}
                  />
                  <CartItemDetails
                    primary={"Delivery Charges"}
                    secondary={
                      <>
                        <span
                          style={{
                            textDecoration: "line-through",
                            fontWeight: "450",
                            marginRight: "3px",
                          }}
                        >
                          40
                        </span>
                        <span
                          style={{
                            fontSize: "14px",
                            color: "green",
                            fontWeight: "500",
                          }}
                        >
                          Free
                        </span>
                      </>
                    }
                  />
                  <CartItemDetails
                    primary={"Grand Total"}
                    secondary={CartReduxService.calculateGrandTotal(
                      products
                    ).toFixed(2)}
                  />
                  <Divider variant="middle" sx={{ my: 2 }} />
                  <Box sx={{}}>
                    <CheckoutButton
                      onClick={() => clickCheckOut()}
                      primaryText={"Checkout"}
                      secondaryText={
                        <Typography
                          sx={{ fontFamily: "Ubuntu", fontWeight: "550" }}
                        >
                          Price : &#8377;
                          {CartReduxService.calculateGrandTotal(
                            products
                          ).toFixed(2)}
                        </Typography>
                      }
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: "70vh",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                maxWidth: "500px",
                width: { xs: "233px", sm: "270px", md: "350px" },
                display: "flex",
                justifyContent: "center",
              }}
            >
              <EmptyCart />
            </Box>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "17px", sm: "20px" },
                fontFamily: "Ubuntu",
                border: "1px groove #0d0d0d",
                borderTop: "0px dotted transparent",
                background: "#f5f5f5",
                letterSpacing: 1,
                p: 3,
                py: 1,
                mb: 1,
                pt: 2,
                borderRadius: "0 0 10px 10px",
              }}
            >
              yOuR cARt iS EmPtY
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
};

export default CartPage;
